import { clamp } from "../core/math";
import { smoothSet } from "./envelope";
import { DelayEffect, type DelayParams } from "./effects/delay";
import { ReverbEffect, type ReverbParams } from "./effects/reverb";
import { peakOf } from "./meter";
import { Mixer } from "./mixer";

/**
 * The shared audio engine (AGENT.md §7). Framework-independent: accepts any
 * BaseAudioContext, so the same graph renders in a browser AudioContext or an
 * OfflineAudioContext (tests, future export).
 *
 *   tracks ─► main bus ─┐
 *   sends ─► reverb ────┤
 *   sends ─► delay  ────┼─► master gain ─► limiter ─► destination
 *   click ─► monitor ───┘
 */
export class AudioEngine {
  readonly mainBus: GainNode;
  readonly monitorBus: GainNode;
  readonly reverb: ReverbEffect;
  readonly delay: DelayEffect;
  readonly mixer: Mixer;
  private master: GainNode;
  private limiter: DynamicsCompressorNode;
  private masterMeter: AnalyserNode;
  private masterBuf: Float32Array<ArrayBuffer>;
  private disposed = false;

  constructor(readonly ctx: BaseAudioContext, opts: { reverb?: Partial<ReverbParams>; delay?: Partial<DelayParams>; masterVolume?: number } = {}) {
    this.master = ctx.createGain();
    this.master.gain.value = clamp(opts.masterVolume ?? 0.8, 0, 1);
    // Soft limiter: protects ears and speakers from summed peaks.
    this.limiter = ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -3;
    this.limiter.knee.value = 0;
    this.limiter.ratio.value = 20;
    this.limiter.attack.value = 0.002;
    this.limiter.release.value = 0.1;
    this.master.connect(this.limiter).connect(ctx.destination);
    this.masterMeter = ctx.createAnalyser();
    this.masterMeter.fftSize = 1024;
    this.masterBuf = new Float32Array(this.masterMeter.fftSize);
    this.limiter.connect(this.masterMeter);

    this.mainBus = ctx.createGain();
    this.monitorBus = ctx.createGain();
    this.mainBus.connect(this.master);
    this.monitorBus.connect(this.master);
    this.reverb = new ReverbEffect(ctx, opts.reverb);
    this.delay = new DelayEffect(ctx, opts.delay);
    this.reverb.output.connect(this.master);
    this.delay.output.connect(this.master);
    this.mixer = new Mixer(ctx, this.mainBus, this.reverb.input, this.delay.input);
  }

  get now(): number { return this.ctx.currentTime; }
  get isDisposed(): boolean { return this.disposed; }

  setMasterVolume(v: number): void { smoothSet(this.master.gain, clamp(v, 0, 1), this.ctx); }
  setMonitorVolume(v: number): void { smoothSet(this.monitorBus.gain, clamp(v, 0, 1), this.ctx); }

  /** Current sample peaks (linear): per track and at the master output (after the limiter). */
  readLevels(): { tracks: Record<string, number>; master: number } {
    this.masterMeter.getFloatTimeDomainData(this.masterBuf);
    return { tracks: this.mixer.readPeaks(), master: peakOf(this.masterBuf) };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.mixer.dispose();
    this.reverb.dispose();
    this.delay.dispose();
    for (const n of [this.mainBus, this.monitorBus, this.master, this.limiter, this.masterMeter]) n.disconnect();
  }
}
